import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useCallback, useEffect, useState } from 'react';
import { Link, useMatch } from 'react-router';
import { useEvent } from '../contexts/event-bus';
import { useAppDispatch, useAppSelector } from '../hooks/store-hooks';
import { setFullscreen } from '../state/slices/global-slice';

export function FullscreenComponent() {
    const startInFullScreen = useAppSelector((x) => x.settings.startInFullscreenMode);
    const [startHandled, setStartHandled] = useState(false); // Indicated whether the startup full screen mode value has been handled
    const [showFullscreenActivationPopup, setShowFullscreenActivationPopup] = useState(false);
    const dispatch = useAppDispatch();

    const tryOpenFullscreen = useCallback(async () => {
        if (document.fullscreenElement) {
            return true;
        }

        try {
            await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
        } catch {
            return false;
        }

        return true;
    }, []);

    // Handle start in fullscreen mode
    useEffect(() => {
        if (startInFullScreen && !startHandled) {
            tryOpenFullscreen().then((ok) => {
                if (!ok && !startHandled) {
                    setShowFullscreenActivationPopup(true);
                }
            });
        }
        // Regardless, the startup has been handled
        setStartHandled(true);
    }, [startInFullScreen, startHandled]);

    useEvent(
        'fullscreen',
        useCallback(() => {
            tryOpenFullscreen().then((ok) => {
                if (!ok) {
                    // TODO: could show a notification on failure, but I don't think it deserves a notification tbh...
                }
            });
        }, [tryOpenFullscreen])
    );

    // Sync fullscreen state with redux
    useEffect(() => {
        const handler = () => {
            dispatch(setFullscreen({ enabled: !!document.fullscreenElement }));
        };
        document.addEventListener('fullscreenchange', handler);
        return () => {
            document.removeEventListener('fullscreenchange', handler);
        };
    }, []);

    if (showFullscreenActivationPopup) {
        return <FullscreenPopup onClose={() => setShowFullscreenActivationPopup(false)} onConfirm={() => tryOpenFullscreen().then()} />;
    }

    return <></>;
}

function FullscreenPopup(props: { onClose?: () => void; onConfirm?: () => void }) {
    const isInSettings = useMatch('/settings/*');

    if (isInSettings) {
        return <></>;
    }

    return (
        <Dialog title="Fullscreen mode" onClose={props.onClose}>
            <div>Auto open fullscreen?</div>
            <div className="secondary-text">
                You can disable this popup in the <Link to="/settings?search=Start in fullscreen mode">settings</Link>.
            </div>
            <DialogActionsBar>
                <Button
                    id="fullscreen-popup-yes-btn"
                    themeColor="primary"
                    onClick={() => {
                        props.onConfirm?.();
                        props.onClose?.();
                    }}>
                    Yes
                </Button>
                <Button id="fullscreen-popup-no-btn" onClick={props.onClose}>
                    No
                </Button>
            </DialogActionsBar>
        </Dialog>
    );
}
